import { ArrowRight, Award, BookOpen, CheckCircle2, ClipboardCheck, Clock, ListChecks } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Badge, Button, EmptyState, PageHeader, ProgressBar, QueryError, SectionTitle, Segmented, Spinner, Stat, ruledList, rowHover } from '@/components/ui';
import { cn } from '@/lib/utils';
import { formatDate, relativeTime } from '@/lib/utils';
import type { Course } from '@/types/db';
import { useCompleteResourceAssignment, useCourses, useMyAssignments, useMyCompetencies, useMyProgress, type MyAssignment } from './api';

type Tab = 'assignments' | 'catalog' | 'transcript' | 'competencies';

const CATEGORY_LABELS: Record<string, string> = {
  continuing_education: 'CE',
  protocol_update: 'Protocol update',
  case_study: 'Case study',
  competency: 'Competency',
  orientation: 'Orientation',
  skills: 'Skills',
};

function assignmentLink(a: MyAssignment): string | null {
  switch (a.assignment.kind) {
    case 'course':
      return `/learn/courses/${a.assignment.course_id}`;
    case 'protocol_ack':
      return `/protocols/${a.assignment.protocol_id}`;
    case 'resource':
      return '/resources';
    default:
      return null;
  }
}

function AssignmentsTab() {
  const { data, isPending, isError, error } = useMyAssignments();
  const completeResource = useCompleteResourceAssignment();
  if (isPending) return <Spinner />;
  if (isError) return <QueryError error={error} />;
  const open = data.filter((a) => a.status === 'assigned');
  const done = data.filter((a) => a.status !== 'assigned');
  if (data.length === 0) return <EmptyState icon={<ListChecks className="size-10" />} title="No assignments">Your agency hasn’t assigned anything yet.</EmptyState>;

  const row = (a: MyAssignment) => {
    const overdue = a.status === 'assigned' && a.assignment.due_at && new Date(a.assignment.due_at) < new Date();
    const link = assignmentLink(a);
    return (
      <li key={a.assignment_id} className="flex flex-wrap items-center gap-3 px-1 py-3.5">
        {a.status === 'assigned' ? <Clock className={overdue ? 'size-4 text-red-600' : 'size-4 text-amber-600'} /> : <CheckCircle2 className="size-4 text-emerald-600" />}
        <div className="min-w-0 flex-1">
          <div className="font-medium">{a.assignment.title}</div>
          <div className="text-sm text-stone-500">
            {a.assignment.kind === 'protocol_ack' ? 'Protocol acknowledgment' : a.assignment.kind === 'competency' ? 'Skills evaluation with an educator' : a.assignment.kind === 'resource' ? 'Review document' : 'Course'}
            {a.assignment.due_at && ` · due ${formatDate(a.assignment.due_at)}`}
            {a.completed_at && ` · completed ${formatDate(a.completed_at)}`}
          </div>
          {a.assignment.instructions && a.status === 'assigned' && <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">{a.assignment.instructions}</p>}
        </div>
        {overdue && <Badge tone="danger">Overdue</Badge>}
        {a.status === 'excused' && <Badge tone="neutral">Excused</Badge>}
        {a.status === 'assigned' && a.assignment.kind === 'resource' && (
          <Button size="sm" variant="outline" loading={completeResource.isPending} onClick={() => completeResource.mutate(a.assignment_id)}>
            Mark reviewed
          </Button>
        )}
        {link && a.status === 'assigned' && (
          <Link to={link} className="inline-flex items-center text-sm font-semibold text-stone-950 underline-offset-[3px] hover:underline dark:text-white">
            Start <ArrowRight className="ml-1 size-3.5" />
          </Link>
        )}
      </li>
    );
  };

  return (
    <div className="space-y-8">
      {open.length > 0 && (
        <section>
          <SectionTitle>Open · {open.length}</SectionTitle>
          <ul className={ruledList}>{open.map(row)}</ul>
        </section>
      )}
      {done.length > 0 && (
        <section>
          <SectionTitle>Completed</SectionTitle>
          <ul className={ruledList}>{done.map(row)}</ul>
        </section>
      )}
    </div>
  );
}

export function CourseCard({ course, progress, completed }: { course: Course; progress: number; completed: boolean }) {
  return (
    <Link to={`/learn/courses/${course.id}`} className={cn('group grid gap-x-6 gap-y-2 px-1 py-4 sm:grid-cols-[1fr_10rem]', rowHover)}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">{course.title}</span>
          {course.is_sample && <Badge tone="warning">Sample</Badge>}
          {completed && <Badge tone="success">Completed</Badge>}
        </div>
        {course.description && <p className="mt-1 line-clamp-2 text-sm text-stone-500 dark:text-stone-400">{course.description}</p>}
        <div className="label-caps mt-2 flex flex-wrap gap-x-3 text-stone-500">
          <span>{CATEGORY_LABELS[course.category] ?? course.category}</span>
          {course.ce_hours > 0 && <span>{course.ce_hours} CE hr</span>}
          {course.estimated_minutes && <span>{course.estimated_minutes} min</span>}
          {course.org_id === null && <span>Platform</span>}
        </div>
      </div>
      <div className="flex items-center gap-3 sm:justify-end">
        <ProgressBar value={completed ? 1 : progress} className="flex-1" />
        <span className="w-9 text-right font-mono text-xs tabular-nums text-stone-500">{Math.round((completed ? 1 : progress) * 100)}%</span>
        <ArrowRight className="size-4 text-stone-300 transition group-hover:translate-x-0.5 group-hover:text-stone-900 dark:text-stone-600 dark:group-hover:text-white" />
      </div>
    </Link>
  );
}

function CatalogTab() {
  const courses = useCourses();
  const progress = useMyProgress();
  const moduleCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of progress.data?.modules ?? []) counts.set(m.course_id, (counts.get(m.course_id) ?? 0) + 1);
    return counts;
  }, [progress.data]);
  if (courses.isPending) return <Spinner />;
  if (courses.isError) return <QueryError error={courses.error} />;
  if (courses.data.length === 0) return <EmptyState icon={<BookOpen className="size-10" />} title="No courses available" />;
  const completed = new Set(progress.data?.completions.map((c) => c.course_id));
  return (
    <ul className={ruledList}>
      {courses.data.map((c) => (
        <li key={c.id}>
          <CourseCard course={c} completed={completed.has(c.id)} progress={c.module_count ? (moduleCounts.get(c.id) ?? 0) / c.module_count : 0} />
        </li>
      ))}
    </ul>
  );
}

function TranscriptTab() {
  const progress = useMyProgress();
  const courses = useCourses();
  if (progress.isPending) return <Spinner />;
  const completions = progress.data?.completions ?? [];
  const since = Date.now() - 730 * 86400000;
  const hours24 = completions.filter((c) => new Date(c.completed_at).getTime() > since).reduce((s, c) => s + Number(c.ce_hours), 0);
  const title = (id: string) => courses.data?.find((c) => c.id === id)?.title ?? 'Course';
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-6 sm:max-w-md">
        <Stat label="CE hours (24 months)" value={hours24.toFixed(1)} />
        <Stat label="Courses completed" value={completions.length} />
      </div>
      {completions.length === 0 ? (
        <EmptyState icon={<Award className="size-10" />} title="No completed courses yet" />
      ) : (
        <ul className={ruledList}>
            {[...completions]
              .sort((a, b) => b.completed_at.localeCompare(a.completed_at))
              .map((c) => (
                <li key={c.course_id} className="flex items-center gap-3 px-1 py-3">
                  <Award className="size-4 text-emerald-600" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{title(c.course_id)}</div>
                    <div className="text-sm text-stone-500">
                      {formatDate(c.completed_at)}
                      {c.score !== null && ` · score ${Math.round(Number(c.score))}%`}
                    </div>
                  </div>
                  <Badge tone="neutral">{Number(c.ce_hours)} CE hr</Badge>
                </li>
              ))}
        </ul>
      )}
    </div>
  );
}

function CompetenciesTab() {
  const { data, isPending, isError, error } = useMyCompetencies();
  if (isPending) return <Spinner />;
  if (isError) return <QueryError error={error} />;
  if (data.competencies.length === 0) return <EmptyState icon={<ClipboardCheck className="size-10" />} title="No competencies defined" />;
  return (
    <ul className={ruledList}>
        {data.competencies.map((c) => {
          const latest = data.evaluations.find((e) => e.competency_id === c.id);
          const current = latest?.passed && (!latest.expires_at || new Date(latest.expires_at) > new Date());
          const expired = latest?.passed && latest.expires_at && new Date(latest.expires_at) <= new Date();
          return (
            <li key={c.id} className="flex flex-wrap items-center gap-3 px-1 py-3">
              <ClipboardCheck className="size-4 text-stone-400" />
              <div className="min-w-0 flex-1">
                <div className="font-medium">{c.title}</div>
                <div className="text-sm text-stone-500">
                  {latest ? `Last evaluated ${formatDate(latest.evaluated_at)}` : 'Not yet evaluated'}
                  {latest?.expires_at && ` · ${expired ? 'expired' : 'expires'} ${relativeTime(latest.expires_at)}`}
                </div>
              </div>
              {current ? <Badge tone="success">Current</Badge> : expired ? <Badge tone="danger">Expired</Badge> : latest && !latest.passed ? <Badge tone="warning">Remediation</Badge> : <Badge tone="neutral">Due</Badge>}
            </li>
          );
        })}
    </ul>
  );
}

export function LearnPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const tab: Tab = location.pathname.endsWith('/assignments') ? 'assignments' : ((params.get('tab') as Tab) ?? 'assignments');

  return (
    <div>
      <PageHeader title="Learn" subtitle="Assignments, continuing education, and competencies" />
      <div className="scrollbar-none mb-6 overflow-x-auto">
        <Segmented<Tab>
          value={tab}
          onChange={(t) => navigate(t === 'assignments' ? '/learn/assignments' : `/learn?tab=${t}`, { replace: true })}
          options={[
            { value: 'assignments', label: 'Assigned' },
            { value: 'catalog', label: 'Courses' },
            { value: 'transcript', label: 'Transcript' },
            { value: 'competencies', label: 'Competencies' },
          ]}
        />
      </div>
      {tab === 'assignments' && <AssignmentsTab />}
      {tab === 'catalog' && <CatalogTab />}
      {tab === 'transcript' && <TranscriptTab />}
      {tab === 'competencies' && <CompetenciesTab />}
    </div>
  );
}
